import * as React from "react";

import { ReadOnlyTypeahead } from "@impero-com/common/components/typeahead";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { getParentElement } from "@impero-com/common/utils/dom";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { Button, Tooltip, Svg } from "@impero-com/ui";
import RemoveIconSvg from "@impero-com/ui/img/remove.svg";

import type { UserProfile } from "api/generated/model";
import {
    SubsequentAssignmentKind,
    UserGroupView,
    AvailableActions,
    UserOverview,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { UserOrUserGroupTypeahead } from "components/userTypeahead";
import { UserOrUserGroupOverview } from "utils/user";

import { ControlAssignmentResources } from "../types";

import * as theme from "./theme.css";
import { toUserOrUserGroupOverviewEnum } from "./types";

class ReviewerInput extends React.PureComponent<ReviewerInputProps> {
    render = () => {
        const { props } = this;
        switch (props.type) {
            case "EDIT":
                return <EditableReviewerInput {...props} />;
            case "NOT_AVAILABLE":
                return <NotAvailableReviewerInput {...props} />;
            default:
                return assertNever(props);
        }
    };
}

class EditableReviewerInput extends React.PureComponent<EditableReviewerInputProps> {
    render = () => {
        const { id, label, reviewerKind, resources, onReviewerRemoved, error } =
            this.props;
        let reviewer: UserOrUserGroupOverview | undefined;
        if (reviewerKind) {
            switch (reviewerKind.type) {
                case "User":
                    reviewer = resources.users.find(
                        (userDto) => userDto.resource.id === reviewerKind.userId
                    )!.resource;
                    break;
                case "Pool":
                    reviewer = resources.userGroups.find(
                        (groupDto) =>
                            groupDto.resource.id === reviewerKind.userGroupId
                    )!.resource;
                    break;
                default:
                    return assertNever(reviewerKind);
            }
        } else {
            reviewer = undefined;
        }
        return (
            <BaseFormDisplay
                {...{ label, error }}
                className={theme.assigneeField}
            >
                <div className={theme.reviewerContainer}>
                    <UserOrUserGroupTypeahead
                        value={reviewer}
                        onSelect={this.handleReviewerChanged}
                        id={id}
                        className={theme.reviewerTypeahead}
                        getPopupContainer={getParentElement}
                    />
                    <RemoveReviewerButton
                        id={`${id}-remove-button`}
                        onReviewerRemoved={onReviewerRemoved}
                    />
                </div>
            </BaseFormDisplay>
        );
    };

    private handleReviewerChanged = (rawReviewer: UserOrUserGroupOverview) => {
        const { onUserAdded, onUserGroupAdded } = this.props;
        let newReviewerKind: SubsequentAssignmentKind;
        const reviewer = toUserOrUserGroupOverviewEnum(rawReviewer);
        switch (reviewer.type) {
            case "User":
                onUserAdded(reviewer.user);
                newReviewerKind = {
                    type: "User",
                    userId: reviewer.user.id,
                };
                break;
            case "UserGroup":
                onUserGroupAdded(reviewer.userGroup);
                newReviewerKind = {
                    type: "Pool",
                    userGroupId: reviewer.userGroup.id,
                };
                break;
            default:
                assertNever(reviewer);
        }
        this.onReviewerChanged(newReviewerKind);
    };

    private onReviewerChanged = (reviewerKind: SubsequentAssignmentKind) => {
        this.props.onReviewerChanged(reviewerKind);
    };
}

class NotAvailableReviewerInput extends React.PureComponent<NotAvailableReviewerInputProps> {
    render = () => {
        const { id, label, typaheadTooltipText } = this.props;
        return (
            <BaseFormDisplay {...{ label }}>
                <Tooltip title={typaheadTooltipText}>
                    <div className={theme.reviewerContainer}>
                        <ReadOnlyTypeahead
                            {...{ id }}
                            className={theme.reviewerTypeahead}
                        />
                        <RemoveReviewerButton
                            id={`${id}-remove-button`}
                            onReviewerRemoved={undefined}
                        />
                    </div>
                </Tooltip>
            </BaseFormDisplay>
        );
    };
}

class RemoveReviewerButton extends React.PureComponent<{
    id: string;
    onReviewerRemoved: (() => void) | undefined;
}> {
    render = () => {
        const { id, onReviewerRemoved } = this.props;
        const disabled = onReviewerRemoved === undefined;
        return (
            <Button
                {...{ id, disabled }}
                variant="link"
                onClick={onReviewerRemoved}
                className={
                    disabled ? theme.disabledRemoveButton : theme.removeButton
                }
            >
                <Svg className={theme.removeIcon} src={RemoveIconSvg} />
            </Button>
        );
    };
}

interface BaseReviewerInputProps {
    id: string;
    label: string;
}

interface EditableReviewerInputProps extends BaseReviewerInputProps {
    type: "EDIT";
    reviewerKind: SubsequentAssignmentKind | undefined;
    resources: ControlAssignmentResources;
    ajaxManager: AjaxManager;
    availableActions: AvailableActions;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile;
    onUserAdded: (user: UserOverview) => void;
    onUserGroupAdded: (userGroup: UserGroupView) => void;
    onReviewerChanged: (reviewerKind: SubsequentAssignmentKind) => void;
    onReviewerRemoved: (() => void) | undefined;
    error?: string;
}

interface NotAvailableReviewerInputProps extends BaseReviewerInputProps {
    type: "NOT_AVAILABLE";
    typaheadTooltipText: string;
}

type ReviewerInputProps =
    | EditableReviewerInputProps
    | NotAvailableReviewerInputProps;

class FirstReviewerSection extends React.PureComponent<FirstReviewerSectionProps> {
    render = () => {
        const {
            value,
            resources,
            ajaxManager,
            availableActions,
            orgCookie,
            orgProfileCookie,
            onFirstReviewerRemoved,
            onUserGroupAdded,
            onUserAdded,
            error,
        } = this.props;
        return (
            <ReviewerInput
                id="first-reviewer-input"
                type="EDIT"
                label={t("ControlResult_Reviewer")}
                reviewerKind={value}
                {...{
                    resources,
                    ajaxManager,
                    availableActions,
                    orgCookie,
                    orgProfileCookie,
                    onUserGroupAdded,
                    onUserAdded,
                    error,
                }}
                onReviewerChanged={this.handleFirstReviewerChanged}
                onReviewerRemoved={
                    onFirstReviewerRemoved
                        ? this.handleFirstReviewerRemoved
                        : undefined
                }
            />
        );
    };

    private handleFirstReviewerChanged = (
        firstReviewerKind: SubsequentAssignmentKind
    ) => {
        const { onFirstReviewerChanged } = this.props;
        onFirstReviewerChanged(firstReviewerKind);
    };

    private handleFirstReviewerRemoved = () => {
        const { onFirstReviewerRemoved } = this.props;
        if (!onFirstReviewerRemoved) {
            throw new Error(
                "Cannot invoke handleFirstReviewerRemoved when there is no removal callback"
            );
        }
        if (onFirstReviewerRemoved) {
            onFirstReviewerRemoved();
        }
    };
}

interface FirstReviewerSectionProps {
    value: SubsequentAssignmentKind | undefined;
    resources: ControlAssignmentResources;
    ajaxManager: AjaxManager;
    availableActions: AvailableActions;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile;
    onUserAdded: (user: UserOverview) => void;
    onUserGroupAdded: (userGroup: UserGroupView) => void;
    onFirstReviewerChanged: (reviewerKind: SubsequentAssignmentKind) => void;
    onFirstReviewerRemoved: (() => void) | undefined;
    error?: string;
}

class EditableSecondReviewerSection extends React.PureComponent<EditableSecondReviewerSectionProps> {
    render = () => {
        const {
            value,
            resources,
            ajaxManager,
            availableActions,
            orgCookie,
            orgProfileCookie,
            onSecondReviewerRemoved,
            onUserGroupAdded,
            onUserAdded,
            error,
        } = this.props;
        return (
            <ReviewerInput
                type="EDIT"
                id="second-reviewer-input"
                label={t("Control_SecondReviewer")}
                reviewerKind={value}
                {...{
                    resources,
                    ajaxManager,
                    availableActions,
                    orgCookie,
                    orgProfileCookie,
                    onUserGroupAdded,
                    onUserAdded,
                    error,
                }}
                onReviewerChanged={this.handleSecondReviewerChanged}
                onReviewerRemoved={onSecondReviewerRemoved}
            />
        );
    };

    private handleSecondReviewerChanged = (
        secondReviewerKind: SubsequentAssignmentKind
    ) => {
        const { onSecondReviewerChanged } = this.props;
        onSecondReviewerChanged(secondReviewerKind);
    };
}

class SecondReviewerSection extends React.PureComponent<SecondReviewerSectionProps> {
    render = () => {
        const { props } = this;
        switch (props.type) {
            case "EDIT":
                return <EditableSecondReviewerSection {...props} />;
            case "NOT_AVAILABLE":
                return (
                    <NotAvailableReviewerInput
                        id="second-reviewer-input"
                        type="NOT_AVAILABLE"
                        label={t("Control_SecondReviewer")}
                        typaheadTooltipText={t(
                            "CreateControl_Assignees_SecondReviewerNotAvailable"
                        )}
                    />
                );
            default:
                return assertNever(props);
        }
    };
}

interface EditableSecondReviewerSectionProps {
    type: "EDIT";
    value: SubsequentAssignmentKind | undefined;
    resources: ControlAssignmentResources;
    ajaxManager: AjaxManager;
    availableActions: AvailableActions;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile;
    onUserAdded: (user: UserOverview) => void;
    onUserGroupAdded: (userGroup: UserGroupView) => void;
    onSecondReviewerChanged: (reviewerKind: SubsequentAssignmentKind) => void;
    onSecondReviewerRemoved: (() => void) | undefined;
    error?: string;
}

interface NotAvailableSecondReviewerSectionProps {
    type: "NOT_AVAILABLE";
}

type SecondReviewerSectionProps =
    | EditableSecondReviewerSectionProps
    | NotAvailableSecondReviewerSectionProps;

export { FirstReviewerSection, SecondReviewerSection };
